<template>
  <div
    class="temperature-status border-2 border-gray-200 p-6 shadow-md transition-all duration-300"
    :class="hasValidValue ? [statusClasses, statusBorderAccent] : ''"
  >
    <!-- Conteúdo real quando há valor -->
    <template v-if="hasValidValue">
      <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-baseline gap-2">
          <span class="text-5xl font-bold tabular-nums tracking-tight" :class="valueTextClass">
            {{ displayValue }}
          </span>
          <span class="text-xl font-semibold text-gray-500">°C</span>
        </div>
        <div class="flex items-center gap-3">
          <div
            class="flex items-center gap-2.5 px-5 py-3 text-base font-bold"
            :class="statusBadgeClasses"
          >
            <component :is="statusIcon" class="h-6 w-6 shrink-0" />
            <span>{{ statusLabel }}</span>
          </div>
        </div>
      </div>
      <template v-if="setpoint != null && !Number.isNaN(Number(setpoint))">
        <p class="mt-4 text-base text-gray-600">
          Limite crítico: <strong class="text-lg">{{ Number(setpoint).toFixed(0) }} °C</strong>
        </p>
        <div class="mt-3 h-3 w-full overflow-hidden bg-gray-200">
          <div
            class="h-full transition-all duration-500"
            :class="barFillClasses"
            :style="barStyle"
          />
        </div>
      </template>
    </template>

    <!-- Shimmer: ocupa o mesmo espaço enquanto aguarda dado -->
    <template v-else>
      <ShimmerStatusCard />
    </template>
  </div>
</template>

<script setup lang="ts">
import ShimmerStatusCard from '@/components/Globals/Feedbacks/ShimmerStatusCard.vue'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Temperatura atual (tempo real) */
    value: number | null | undefined
    /** Limite crítico: acima disso = não aceitável */
    setpoint?: number | null
    /** Valor máximo para a barra de referência */
    maxBar?: number
  }>(),
  {
    setpoint: null,
    maxBar: 50,
  }
)

const hasValidValue = computed(() => props.value != null && !Number.isNaN(Number(props.value)))

const displayValue = computed(() => {
  if (!hasValidValue.value) return '—'
  return Number(props.value).toFixed(1)
})

const displayMax = computed(() => {
  if (props.setpoint == null || Number.isNaN(Number(props.setpoint))) return props.maxBar
  return Math.max(props.maxBar, Number(props.setpoint) + 10)
})

/** Temperatura está acima ou igual ao limite = não aceitável */
const isAboveSetpoint = computed(() => {
  if (!hasValidValue.value || props.setpoint == null) return false
  return Number(props.value) >= Number(props.setpoint)
})

const statusLabel = computed(() => {
  if (props.setpoint == null) return 'Sem limite definido'
  return isAboveSetpoint.value ? 'Acima do limite' : 'Dentro do limite'
})

const statusIcon = computed(() => (isAboveSetpoint.value ? ExclamationTriangleIcon : CheckCircleIcon))

const statusClasses = computed(() => {
  if (isAboveSetpoint.value) {
    return 'border-red-300 bg-red-50'
  }
  return 'border-green-200 bg-green-50/70'
})

const statusBorderAccent = computed(() => {
  if (isAboveSetpoint.value) return 'border-l-4 border-l-red-500'
  return 'border-l-4 border-l-green-500'
})

const valueTextClass = computed(() => {
  if (!hasValidValue.value) return 'text-gray-400'
  return isAboveSetpoint.value ? 'text-red-700' : 'text-gray-900'
})

const statusBadgeClasses = computed(() => {
  if (isAboveSetpoint.value) {
    return 'bg-red-100 text-red-800'
  }
  return 'bg-green-100 text-green-800'
})

/** Barra: preenche até o valor atual; referência é o setpoint. */
const barFillPercent = computed(() => {
  if (!hasValidValue.value || displayMax.value <= 0) return 0
  const v = Number(props.value)
  const max = displayMax.value
  return Math.min(100, (v / max) * 100)
})

const barStyle = computed(() => ({
  width: `${barFillPercent.value}%`,
}))

const barFillClasses = computed(() => {
  if (!hasValidValue.value) return 'bg-gray-300'
  return isAboveSetpoint.value ? 'bg-red-500' : 'bg-green-500'
})
</script>
